import { useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { TableRowSkeleton } from './LoadingSkeleton';
import { EmptyState } from './EmptyState';
import type { LucideIcon } from 'lucide-react';

export interface ColumnDef<T> {
  key: string;
  header: string;
  /** Preferred: full row accessor function */
  accessor?: (row: T) => React.ReactNode;
  /** Legacy: value-level render (falls back when accessor is absent) */
  render?: (value: unknown, row: T) => React.ReactNode;
  sortable?: boolean;
  className?: string;
  headerClassName?: string;
}

export interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  loading?: boolean;
  /** Preferred key extractor */
  rowKey?: (row: T) => string;
  /** Legacy alias for rowKey */
  getRowKey?: (row: T) => string;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyIcon?: LucideIcon;
  onRowClick?: (row: T) => void;
  stickyHeader?: boolean;
  compact?: boolean;
  className?: string;
}

type SortDirection = 'asc' | 'desc' | null;

export function DataTable<T>({
  columns,
  data,
  loading = false,
  rowKey,
  getRowKey,
  emptyTitle = 'No results',
  emptyDescription,
  emptyIcon,
  onRowClick,
  stickyHeader = false,
  compact = false,
  className,
}: DataTableProps<T>) {
  const [sortKey, setSortKey]   = useState<string | null>(null);
  const [sortDir, setSortDir]   = useState<SortDirection>(null);

  const resolveKey = (row: T, idx: number): string => {
    const fn = rowKey ?? getRowKey;
    if (fn) return fn(row);
    return String(idx);
  };

  function renderCell(col: ColumnDef<T>, row: T): React.ReactNode {
    if (col.accessor) return col.accessor(row);
    if (col.render)   return col.render((row as Record<string, unknown>)[col.key], row);
    const val = (row as Record<string, unknown>)[col.key];
    return val != null ? String(val) : '—';
  }

  function handleSort(key: string) {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortKey(null);
      setSortDir(null);
    }
  }

  const cellPadding = compact ? 'px-4 py-2' : 'px-4 py-3';

  return (
    <div className={cn('overflow-x-auto rounded-[var(--radius-lg)] border border-[var(--color-border)]', className)}>
      <table className="w-full text-sm" role="grid">
        <thead className={cn('bg-[var(--color-bg-tertiary)] border-b border-[var(--color-border)]', stickyHeader && 'sticky top-0 z-10')}>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={cn(
                  cellPadding,
                  'text-left text-xs font-semibold text-[var(--color-text-secondary)] uppercase tracking-wider whitespace-nowrap',
                  col.sortable && 'cursor-pointer select-none hover:text-[var(--color-text-primary)] transition-colors',
                  col.headerClassName,
                )}
                onClick={col.sortable ? () => handleSort(col.key) : undefined}
                aria-sort={
                  sortKey === col.key
                    ? sortDir === 'asc' ? 'ascending' : 'descending'
                    : col.sortable ? 'none' : undefined
                }
              >
                <div className="flex items-center gap-1.5">
                  {col.header}
                  {col.sortable && (
                    <span aria-hidden="true">
                      {sortKey === col.key ? (
                        sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      ) : (
                        <ChevronsUpDown className="w-3 h-3 opacity-50" />
                      )}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--color-border)]">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <TableRowSkeleton key={i} cols={columns.length} />
            ))
          ) : data.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>
                <EmptyState
                  icon={emptyIcon}
                  title={emptyTitle}
                  description={emptyDescription}
                />
              </td>
            </tr>
          ) : (
            data.map((row, idx) => (
              <tr
                key={resolveKey(row, idx)}
                role={onRowClick ? 'button' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={onRowClick ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick(row);
                  }
                } : undefined}
                className={cn(
                  'bg-[var(--color-bg-secondary)] transition-colors',
                  onRowClick && 'cursor-pointer hover:bg-[var(--color-bg-tertiary)]',
                )}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cn(
                      cellPadding,
                      'text-[var(--color-text-primary)]',
                      col.className,
                    )}
                  >
                    {renderCell(col, row)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
